import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import Currency from "components/format/currency/currency";
import { DayRange, formatQuantity, PeriodTotals, PresetId, PRESETS } from "./sales-by-period";

interface Props {
  preset: PresetId;
  /** The days the figures cover; null = whole history. */
  range: DayRange | null;
  totals: PeriodTotals;
  unconvertedDocumentCount: number;
}

export default function PeriodSummary({ preset, range, totals, unconvertedDocumentCount }: Props) {
  const { t } = useTranslation();
  // Dates are digits, so they can sit inside the sentence without any inflection.
  const periodLabel = range
    ? t("customer_products_period_with_dates", { label: t(summaryLabelKey(preset)), from: range.from, to: range.to })
    : t("customer_products_preset_all");

  return (
    <div className="flex flex-col gap-1" data-testid="customer-products-summary">
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm text-gray-600">
        <span className="text-base font-semibold text-gray-900">{periodLabel}</span>
        <span>
          {t("customer_products_count")}:{" "}
          <span className="font-medium text-gray-900">{totals.productCount}</span>
        </span>
        {totals.quantityByUnit.length > 0 && (
          <span>
            {t("customer_products_quantity")}:{" "}
            <span className="font-medium text-gray-900">
              {totals.quantityByUnit.map(({ unit, quantity }, index) => (
                <Fragment key={unit ?? "no-unit"}>
                  {index > 0 && " · "}
                  {formatQuantity(quantity)} {unit ?? t("customer_products_no_unit")}
                </Fragment>
              ))}
            </span>
          </span>
        )}
        <span>
          {t("customer_products_net_amount")}:{" "}
          <span className="font-medium text-gray-900">
            <Currency value={totals.netEur} isSymbol isDecimal />
          </span>
        </span>
        {totals.returnedEur > 0 && (
          <span>
            {t("customer_products_returned")}:{" "}
            <Currency value={totals.returnedEur} isSymbol isDecimal />
          </span>
        )}
      </div>
      {unconvertedDocumentCount > 0 && (
        <p className="text-xs text-gray-500">
          {t("customer_products_unconverted", { value: unconvertedDocumentCount })}
        </p>
      )}
    </div>
  );
}

/** The heading of a period: the button's own text, except "Pasirinkti…", which reads as an action, not a name. */
function summaryLabelKey(preset: PresetId): string {
  if (preset === "custom") return "customer_products_period_custom_label";
  const button = PRESETS.find((candidate) => candidate.id === preset);
  return button ? button.labelKey : "customer_products_preset_all";
}
